/**
 * Feb 2026 · Unit-aware number formatting — the single source of truth
 * for how quantities and currencies get rendered anywhere in the app
 * (dashboards, view pages, forms, print/PDF helpers).
 *
 * Formatting rules — CODIFY THESE ONCE. Every screen imports from here.
 *   Meters (MTR/M/MTRS)         → whole number, Indian commas   → 1,23,456
 *   Kilograms (KG/KGS)          → 3 decimals, Indian commas     → 1,234.567
 *   Grams (G/GM/GMS)            → 2 decimals, Indian commas     → 123.45
 *   Rupees (INR / ₹)            → 2 decimals, Indian commas     → ₹1,23,456.78
 *   USD ($)                     → 4 decimals, US commas          → $1.2345
 *   Anything else               → 2 decimals, US commas          → 12,345.67
 *
 * Callers pass the raw number and the unit / currency string exactly as
 * stored in the DB (case-insensitive, tolerant of trailing s / punctuation).
 */

// Rule table — extend by adding aliases to the right side of the map.
const UNIT_DECIMALS = Object.freeze({
  MTR: 0, MTRS: 0, M: 0, METER: 0, METERS: 0, METRE: 0, METRES: 0,
  KG: 3, KGS: 3, KILO: 3, KILOS: 3, KILOGRAM: 3, KILOGRAMS: 3,
  G: 2, GM: 2, GMS: 2, GRAM: 2, GRAMS: 2, GRM: 2,
});

// Cheap cache · Intl.NumberFormat init is not free and gets called a lot
// on wide tables (Sales Orders grid renders thousands of cells).
const _fmtCache = new Map();
function _nf(locale, decimals) {
  const k = `${locale}:${decimals}`;
  let f = _fmtCache.get(k);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    _fmtCache.set(k, f);
  }
  return f;
}

function _norm(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Format a quantity for a given unit. Returns "" for null / undefined so
 *  empty table cells stay empty instead of showing "0". */
export function fmtQty(v, unit) {
  if (v === null || v === undefined || v === "") return "";
  const u = String(unit || "MTR").trim().toUpperCase().replace(/[^A-Z]/g, "");
  const decimals = UNIT_DECIMALS[u] ?? 0;
  return _nf("en-IN", decimals).format(_norm(v));
}

/** Rupees · Indian grouping · 2 decimals · optional ₹ prefix. */
export function fmtInr(v, { symbol = true } = {}) {
  const s = _nf("en-IN", 2).format(_norm(v));
  return symbol ? `₹${s}` : s;
}

/** USD · US grouping · 4 decimals · optional $ prefix. */
export function fmtUsd(v, { symbol = true } = {}) {
  const s = _nf("en-US", 4).format(_norm(v));
  return symbol ? `$${s}` : s;
}

/** Currency dispatch — pass the currency code stored on the doc. */
export function fmtCurrency(v, currency = "INR", opts = {}) {
  const c = String(currency || "INR").trim().toUpperCase();
  if (c === "INR" || c === "₹" || c === "RS" || c === "RS." || c === "RUPEES") return fmtInr(v, opts);
  if (c === "USD" || c === "$" || c === "US$") return fmtUsd(v, opts);
  // Foreign currencies with no explicit rule fall back to 2-dp US grouping,
  // prefixed with the ISO code so the caller always knows what they're
  // looking at (e.g. "EUR 1,234.56").
  const s = _nf("en-US", 2).format(_norm(v));
  return opts.symbol === false ? s : `${c} ${s}`;
}

/** Whole-number Indian grouping (useful for counters / IDs). */
export function fmtInt(v) {
  return _nf("en-IN", 0).format(_norm(v));
}
